<template>
  <div class="home-view">
    <!-- Hero Section -->
    <section
        class="hero-section hero-toolbox"
        @mousemove="onHeroMove"
        @mouseleave="onHeroLeave"
    >
      <div class="hero-grid-pattern"></div>
      <div class="hero-toolbox-inner">
        <span class="hero-eyebrow">EPRINTTOOLS · 印前工具箱</span>
        <h1 class="hero-title">你的印前工具箱。<br><em>需要什么，打开什么。</em></h1>
        <p class="hero-subtitle">一个入口，装下印前工作里的每一件小事。工具会持续增加，按你的工作方式扩展。</p>

        <div class="hero-actions">
          <button class="cta-button" @click="$router.push('/tools')">
            <span>打开工具箱</span>
            <el-icon class="cta-icon"><ArrowRight /></el-icon>
          </button>
          <button class="cta-button-secondary" @click="$router.push('/solutions')">
            <span>查看解决方案</span>
            <el-icon class="cta-icon"><ArrowRight /></el-icon>
          </button>
        </div>

        <div class="hero-stage">
          <div class="hero-stage-grid"></div>
          <div class="hero-stage-glow"></div>
          <div class="hero-stage-ring hero-stage-ring--one"></div>
          <div class="hero-stage-ring hero-stage-ring--two"></div>
          <span class="hero-stage-chip hero-stage-chip--aip">AIP</span>
          <span class="hero-stage-chip hero-stage-chip--exe">EXE</span>
          <div
            class="hero-dock"
            aria-hidden="true"
            :style="{ transform: `translate3d(${heroTilt.x}px, ${heroTilt.y}px, 0)` }"
          >
          <div
              v-for="tool in dockTools"
              :key="tool.key"
              class="dock-tile"
              :class="`dock-tile--${tool.tone}`"
          >
            <svg v-if="tool.key === 'shape'" viewBox="0 0 24 24" fill="none">
              <rect x="3.5" y="3.5" width="7" height="7" rx="2" stroke="currentColor" stroke-width="1.8"/>
              <rect x="13.5" y="3.5" width="7" height="7" rx="2" stroke="currentColor" stroke-width="1.8"/>
              <rect x="3.5" y="13.5" width="7" height="7" rx="2" stroke="currentColor" stroke-width="1.8"/>
              <rect x="13.5" y="13.5" width="7" height="7" rx="2" fill="currentColor"/>
            </svg>
            <svg v-else-if="tool.key === 'compare'" viewBox="0 0 24 24" fill="none">
              <rect x="3.5" y="4" width="7" height="16" rx="2" stroke="currentColor" stroke-width="1.8"/>
              <rect x="13.5" y="4" width="7" height="16" rx="2" stroke="currentColor" stroke-width="1.8"/>
              <path d="M10.5 12h3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
            <svg v-else-if="tool.key === 'barcode'" viewBox="0 0 24 24" fill="none">
              <path d="M4 5v14M7.5 5v14M11 5v9M14.5 5v14M18 5v9M20.5 5v14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
            <svg v-else-if="tool.key === 'color'" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8"/>
              <path d="M12 3.5V12H20.5" stroke="currentColor" stroke-width="1.8"/>
              <path d="M12 12V20.5H3.5" stroke="currentColor" stroke-width="1.8"/>
            </svg>
            <svg v-else-if="tool.key === 'dieline'" viewBox="0 0 24 24" fill="none">
              <path d="M4 8.5 12 4l8 4.5v7L12 20l-8-4.5v-7Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
              <path d="M4 8.5 12 13l8-4.5M12 13v7" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="tool.key === 'type'" viewBox="0 0 24 24" fill="none">
              <path d="M5 6V4h14v2M12 4v16M8.5 20h7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="dock-tile dock-tile--more"><span>+</span></div>
          </div>
          <span class="hero-stage-label">TOOLKIT / 2026</span>
        </div>

        <p class="hero-meta">Windows 10/11 · Illustrator 2019+</p>
      </div>
      <div class="hero-fade" aria-hidden="true"></div>
    </section>


    <!-- 精选工具 -->
    <section class="tools-section">
      <div class="tools-section-inner">
        <div class="home-section-head">
          <div class="home-section-copy">
            <span class="home-eyebrow">01 / CURATED TOOLKIT</span>
            <h2 class="home-title">精选工具</h2>
            <p class="home-lead">为包装印刷行业精选的 4 款 AIP 插件，覆盖对象替换、条码生成与色彩管理。全部工具、EXE 程序与定制案例在一个入口。</p>
          </div>
          <div class="home-section-side">
            <span class="home-count">{{ (homeTools.length || 4).toString().padStart(2, '0') }}</span>
            <span class="home-count-label">款 AIP 插件</span>
            <router-link class="home-text-link" to="/tools">全部工具与案例 →</router-link>
          </div>
        </div>

        <div v-if="homeToolsLoading" class="home-tools-loading">正在加载精选工具…</div>

        <div v-else-if="homeTools.length" class="tools-grid">
          <div
              v-for="(tool, index) in homeTools"
              :key="homeToolKey(tool)"
              class="tool-card hover-lift group"
              @click="goToHomeTool(tool)"
              :style="{ '--card-delay': (index * 0.1) + 's' }"
          >
            <div class="tool-badge tool-badge--aip">AIP</div>
            <div class="tool-trial" :class="{ 'is-free': toolTrialText(tool) === '永久免费' }">{{ toolTrialText(tool) }}</div>
            <div class="tool-image-wrap">
              <img
                  v-if="homeHeroGif(tool)"
                  :src="homeHeroGif(tool)"
                  :alt="`${tool.name} 功能演示`"
                  class="home-tool-gif"
                  loading="lazy"
              />
              <PluginCover
                  v-else
                  :cover="homeCover(tool)"
                  :accent="homeAccent(tool)"
                  class="home-tool-cover"
              />
              <div class="tool-image-overlay"></div>
              <div class="tool-image-icon">
                <ToolLogo
                    :tool="tool"
                    :accent="homeAccent(tool)"
                    :failed="failedHomeIcons"
                    @fail="markHomeIconFailed"
                />
              </div>
            </div>
            <div class="tool-body">
              <h3 class="tool-name">{{ tool.name }}</h3>
              <p class="tool-desc">{{ homeTagline(tool) }}</p>
              <div class="home-tool-chips">
                <span v-for="chip in homeChips(tool)" :key="chip">{{ chip }}</span>
              </div>
              <div class="tool-action">
                <span class="tool-action-label" :class="'tool-action-label--' + homeTone(index)">查看详情</span>
                <div class="tool-action-arrow" :class="'tool-action-arrow--' + homeTone(index)">
                  <el-icon><ArrowRight /></el-icon>
                </div>
              </div>
            </div>
            <div class="tool-bar" :class="'tool-bar--' + homeTone(index)"></div>
          </div>
        </div>

        <div v-else class="home-tools-empty">
          <p>精选工具加载失败，去 <router-link to="/tools">全部工具</router-link> 查看。</p>
        </div>

      </div>
    </section>


    <!-- 解决方案 (全新样式，与 HTML 一致) -->
    <section class="solutions-section">
      <div class="solutions-section-bg"></div>
      <div class="solutions-section-inner solutions-editorial">
        <div class="solutions-intro">
          <span class="home-eyebrow">02 / SOLUTIONS</span>
          <h2 class="home-title solutions-title">从设计到生产，<br><span>每条链路都有方案</span></h2>
          <p class="home-lead">覆盖包装印刷全链路：设计创作、质量验证、生产就绪与流程自动化，把零散工具串成可落地的工作流。</p>
          <router-link class="home-text-link solutions-link" to="/solutions">查看全部解决方案 →</router-link>
        </div>

        <div class="solutions-grid solutions-grid--editorial">
          <!-- 解决方案 1：设计与创作 -->
          <div
              class="solution-card hover-lift group"
              @click="$router.push('/solutions')"
              style="--card-delay: 0s"
          >
            <span class="solution-index">01</span>
            <div class="solution-top-bar solution-top-bar--blue"></div>
            <div class="solution-icon solution-icon--blue">
              <el-icon><Edit /></el-icon>
            </div>
            <h3 class="solution-title">设计与创作</h3>
            <p class="solution-desc">专业的包装设计、3D建模和创意工具，提升设计效率</p>
            <div class="solution-link">查看方案 <el-icon><ArrowRight /></el-icon></div>
          </div>

          <!-- 解决方案 2：质量验证 -->
          <div
              class="solution-card hover-lift group"
              @click="$router.push('/solutions')"
              style="--card-delay: 0.1s"
          >
            <span class="solution-index">02</span>
            <div class="solution-top-bar solution-top-bar--orange"></div>
            <div class="solution-icon solution-icon--orange">
              <el-icon><Check /></el-icon>
            </div>
            <h3 class="solution-title">质量验证</h3>
            <p class="solution-desc">自动化校验、能效标签验证和合规性检查工具</p>
            <div class="solution-link">查看方案 <el-icon><ArrowRight /></el-icon></div>
          </div>

          <!-- 解决方案 3：生产就绪 -->
          <div
              class="solution-card hover-lift group"
              @click="$router.push('/solutions')"
              style="--card-delay: 0.2s"
          >
            <span class="solution-index">03</span>
            <div class="solution-top-bar solution-top-bar--rose"></div>
            <div class="solution-icon solution-icon--rose">
              <el-icon><Setting /></el-icon>
            </div>
            <h3 class="solution-title">生产就绪</h3>
            <p class="solution-desc">智能刀模、工艺文件和印刷就绪解决方案</p>
            <div class="solution-link">查看方案 <el-icon><ArrowRight /></el-icon></div>
          </div>

          <!-- 解决方案 4：流程自动化 -->
          <div
              class="solution-card hover-lift group"
              @click="$router.push('/solutions')"
              style="--card-delay: 0.3s"
          >
            <span class="solution-index">04</span>
            <div class="solution-top-bar solution-top-bar--purple"></div>
            <div class="solution-icon solution-icon--purple">
              <el-icon><Opportunity /></el-icon>
            </div>
            <h3 class="solution-title">流程自动化</h3>
            <p class="solution-desc">工作流自动化、批量处理和系统集成方案</p>
            <div class="solution-link">查看方案 <el-icon><ArrowRight /></el-icon></div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { fetchToolCatalog } from '@/api/catalog';
import { AIP_PLUGIN_CONTENT } from '@/constants/aipPluginContent';
import PluginCover from '@/components/tools/PluginCover.vue';
import ToolLogo from '@/components/tools/ToolLogo.vue';
import {
  ArrowRight,
  Edit,
  Check,
  Setting,
  Opportunity,
  Grid,
  Scissor,
  DataAnalysis,
  CollectionTag
} from '@element-plus/icons-vue';

export default {
  name: 'HomeView',

  components: {
    ArrowRight,
    Edit,
    Check,
    Setting,
    Opportunity,
    Grid,
    Scissor,
    DataAnalysis,
    CollectionTag,
    PluginCover,
    ToolLogo
  },

  setup() {
    const router = useRouter();

    const homeImageLoaded = ref(false);
    const compressedHomeImage = ref('');

    const homeTools = ref([]);
    const homeToolsLoading = ref(true);
    const failedHomeIcons = reactive({});
    const heroTilt = reactive({ x: 0, y: 0 });
    const dockTools = [
      { key: 'shape', tone: 'blue' },
      { key: 'compare', tone: 'cyan' },
      { key: 'barcode', tone: 'teal' },
      { key: 'color', tone: 'orange' },
      { key: 'dieline', tone: 'violet' },
      { key: 'type', tone: 'slate' }
    ];

    const loadAndCompressHomeImage = async () => {
      try {
        const imageUrl = '/eprinttools-files/front-end/home1.png';
        compressedHomeImage.value = imageUrl;
        const img = new Image();
        img.onload = () => {
          homeImageLoaded.value = true;
        };
        img.src = imageUrl;
      } catch (error) {
        console.error('首页图片加载失败:', error);
        compressedHomeImage.value = '/eprinttools-files/front-end/home1.png';
        homeImageLoaded.value = true;
      }
    };

    const loadHomeTools = async () => {
      try {
        const res = await fetchToolCatalog();
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        homeTools.value = items
            .filter(item => item.kind === 'AIP')
            .slice(0, 4);
      } catch (error) {
        console.error('首页工具加载失败:', error);
        homeTools.value = [];
      } finally {
        homeToolsLoading.value = false;
      }
    };

    const onHomeImageLoad = () => {
      homeImageLoaded.value = true;
    };

    const goToHomeTool = (tool) => {
      if (tool.kind === 'AIP') {
        router.push(`/tools/aip/${tool.pluginKey}`);
        return;
      }
      router.push({ path: '/products', query: { id: tool.id } });
    };

    const homeToolKey = (tool) => tool.pluginKey || tool.productCode || tool.id;
    const toolTrialText = (tool) => AIP_PLUGIN_CONTENT[tool.pluginKey]?.trialLabel || `${tool.trialDays} 天试用`;
    const homeHeroGif = (tool) => AIP_PLUGIN_CONTENT[tool.pluginKey]?.heroGif || '';
    const homeTagline = (tool) => AIP_PLUGIN_CONTENT[tool.pluginKey]?.tagline || tool.description || '查看产品详情';
    const homeChips = (tool) => AIP_PLUGIN_CONTENT[tool.pluginKey]?.chips || [];
    const homeCover = (tool) => AIP_PLUGIN_CONTENT[tool.pluginKey]?.cover || 'ereplace';
    const homeAccent = (tool) => AIP_PLUGIN_CONTENT[tool.pluginKey]?.accent || '#2F5BFF';
    const homeTone = (index) => ['blue', 'orange', 'indigo', 'warm'][index % 4];
    const markHomeIconFailed = (key) => {
      failedHomeIcons[key] = true;
    };

    const onHeroMove = (event) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const rect = event.currentTarget.getBoundingClientRect();
      heroTilt.x = ((event.clientX - rect.left) / rect.width - 0.5) * 8;
      heroTilt.y = ((event.clientY - rect.top) / rect.height - 0.5) * 6;
    };

    const onHeroLeave = () => {
      heroTilt.x = 0;
      heroTilt.y = 0;
    };

    onMounted(async () => {
      loadAndCompressHomeImage();
      loadHomeTools();
    });

    return {
      homeImageLoaded,
      compressedHomeImage,
      homeTools,
      homeToolsLoading,
      failedHomeIcons,
      heroTilt,
      dockTools,
      onHeroMove,
      onHeroLeave,
      onHomeImageLoad,
      goToHomeTool,
      homeToolKey,
      toolTrialText,
      homeHeroGif,
      homeTagline,
      homeChips,
      homeCover,
      homeAccent,
      homeTone,
      markHomeIconFailed
    };
  }
};
</script>

<style scoped>
/* ============ Root ============ */
.home-view {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #0f172a;
  background: transparent;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

/* ============ Hero (保持不变) ============ */
.hero-section {
  position: relative;
  padding: 110px 20px 90px;
  overflow: hidden;
  background: transparent;
}

.hero-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.6;
  pointer-events: none;
  animation: blobFloat 14s ease-in-out infinite;
}

.hero-blob-blue {
  width: 480px;
  height: 480px;
  background: radial-gradient(circle, rgba(47, 91, 255, 0.42), transparent 70%);
  top: -140px;
  left: -100px;
}

.hero-blob-orange {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.38), transparent 70%);
  top: 20px;
  right: -80px;
  animation-delay: -5s;
}

.hero-blob-small {
  width: 260px;
  height: 260px;
  background: radial-gradient(circle, rgba(91, 124, 255, 0.32), transparent 70%);
  bottom: -80px;
  left: 38%;
  animation-delay: -9s;
}

@keyframes blobFloat {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(24px, -32px) scale(1.06);
  }
}

.hero-grid-pattern {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(47, 91, 255, 0.04) 1px, transparent 1px),
  linear-gradient(90deg, rgba(47, 91, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%);
  pointer-events: none;
}

.hero-editorial {
  padding: 76px 20px 68px;
}

.hero-editorial-inner {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1.03fr) minmax(0, 0.97fr);
  align-items: center;
  gap: 54px;
  width: min(1200px, 100%);
  margin: 0 auto;
}

.hero-editorial .hero-copy {
  max-width: 620px;
  text-align: left;
  animation: fadeInUp 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.hero-editorial .hero-eyebrow {
  display: inline-flex;
  align-items: center;
  margin-bottom: 20px;
}

.hero-editorial .hero-title {
  margin: 0 0 20px;
  background: none;
  -webkit-text-fill-color: currentColor;
  color: #0f172a;
  font-size: clamp(40px, 4.9vw, 60px);
  line-height: 1.06;
  letter-spacing: -0.045em;
}

.hero-editorial .hero-title em {
  color: #2f5bff;
  font-style: normal;
}

.hero-editorial .hero-subtitle {
  max-width: 560px;
  margin: 0 0 28px;
  color: #64748b;
  font-size: 15.5px;
  line-height: 1.8;
}

.hero-editorial .hero-actions {
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 38px;
}

.hero-editorial .hero-stats {
  justify-content: flex-start;
  gap: 28px;
}

.hero-editorial .hero-stat-num {
  background: none;
  -webkit-text-fill-color: currentColor;
  color: #0f172a;
  font-size: 2rem;
  letter-spacing: -0.04em;
}

.hero-editorial .hero-stat-label {
  color: #94a3b8;
  font-size: 11px;
  letter-spacing: 0.04em;
}

.hero-editorial .hero-stat-divider {
  height: 28px;
  background: linear-gradient(180deg, transparent, rgba(47, 91, 255, 0.22), transparent);
}

.hero-visual {
  position: relative;
  animation: fadeInUp 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.12s both;
}

.hero-panel {
  position: relative;
  padding: 16px;
  border: 1px solid #e6eaf2;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 30px 80px -48px rgba(15, 23, 42, 0.38);
  transform: rotate(1deg);
}

.hero-panel-head,
.hero-panel-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 600;
}

.hero-panel-head .mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: 0.08em;
}

.hero-panel-count {
  color: #2f5bff;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.05em;
}

.hero-panel-covers {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  grid-template-rows: repeat(2, 128px);
  gap: 10px;
  margin: 12px 0;
}

.hero-cover {
  position: relative;
  overflow: hidden;
  border: 1px solid #e6eaf2;
  border-radius: 14px;
  background: #f7f8fa;
}

.hero-cover--main {
  grid-row: span 2;
}

.hero-cover :deep(.plugin-cover) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.hero-cover span {
  position: absolute;
  left: 9px;
  bottom: 9px;
  z-index: 2;
  padding: 4px 8px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.86);
  color: #334155;
  font-size: 10px;
  font-weight: 600;
  backdrop-filter: blur(6px);
}

.hero-panel-foot {
  padding-top: 10px;
  border-top: 1px solid #eef1f6;
}

.hero-panel-foot a {
  color: #2f5bff;
  font-weight: 650;
  text-decoration: none;
}

.hero-float {
  position: absolute;
  right: -10px;
  bottom: -16px;
  padding: 9px 13px;
  border: 1px solid #e6eaf2;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 14px 30px -22px rgba(15, 23, 42, 0.5);
  color: #475569;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
}

/* Hero product workbench */
.workbench-window {
  position: relative;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 32px 70px -46px rgba(15, 23, 42, 0.45);
  transform: rotate(-1deg);
}

.workbench-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 16px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;
}

.workbench-dots {
  display: flex;
  gap: 4px;
}

.workbench-dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #dbe3ef;
}

.workbench-title {
  color: #334155;
  font-size: 11px;
  font-weight: 650;
}

.workbench-version {
  margin-left: auto;
  color: #94a3b8;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 9px;
  letter-spacing: 0.04em;
}

.workbench-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 168px;
  gap: 12px;
  padding: 14px;
  background: #f5f7fb;
}

.workbench-canvas {
  position: relative;
  min-height: 326px;
  padding: 24px;
  border: 1px solid #e6ebf3;
  border-radius: 13px;
  background:
      linear-gradient(rgba(47, 91, 255, .035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(47, 91, 255, .035) 1px, transparent 1px),
      #fff;
  background-size: 24px 24px;
}

.canvas-grid {
  display: grid;
  grid-template-columns: repeat(4, 60px);
  justify-content: center;
  align-content: center;
  gap: 14px;
  min-height: 276px;
}

.canvas-shape {
  display: block;
  width: 60px;
  height: 60px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 13px;
  background: rgba(248, 250, 252, .8);
}

.canvas-shape.is-ghost {
  opacity: .58;
}

.canvas-shape.is-match {
  border: 2px solid #dc2626;
  background: rgba(220, 38, 38, .12);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, .08);
}

.canvas-shape.is-replaced {
  border: 2px solid #2f5bff;
  background: #2f5bff;
  box-shadow: 0 8px 16px -10px rgba(47, 91, 255, .8);
}

.canvas-note {
  position: absolute;
  left: 12px;
  bottom: 10px;
  padding: 4px 7px;
  border: 1px solid #e6ebf3;
  border-radius: 6px;
  background: rgba(255, 255, 255, .88);
  color: #64748b;
  font-size: 8px;
  font-weight: 600;
}

.workbench-panel {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 14px;
  border: 1px solid #e6ebf3;
  border-radius: 13px;
  background: #fff;
}

.panel-kicker {
  color: #94a3b8;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 9px;
  letter-spacing: .08em;
}

.panel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 9px;
  border-bottom: 1px solid #f1f4f9;
  color: #64748b;
  font-size: 10px;
}

.panel-row b {
  color: #0f172a;
  font-size: 16px;
  font-weight: 750;
}

.panel-swatch {
  width: 15px;
  height: 15px;
  border-radius: 4px;
  background: #dc2626;
}

.panel-actions {
  display: flex;
  gap: 6px;
}

.panel-btn {
  flex: 1;
  padding: 7px 4px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
}

.panel-btn--primary {
  border-color: #2f5bff;
  background: #2f5bff;
  color: #fff;
}

.panel-hint {
  margin-top: auto;
  color: #b0bccd;
  font-size: 8px;
  line-height: 1.4;
}

.workbench-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-top: 1px solid #eef2f7;
  background: #fbfcfe;
  color: #64748b;
  font-size: 9px;
}

.status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #22c55e;
}

.workbench-float {
  position: absolute;
  right: -10px;
  bottom: -16px;
  padding: 8px 11px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 14px 30px -22px rgba(15, 23, 42, .5);
  color: #475569;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 8px;
  letter-spacing: .04em;
}

.hero-toolbox {
  padding: 82px 20px 64px;
}

.hero-toolbox-inner {
  position: relative;
  z-index: 2;
  max-width: 980px;
  margin: 0 auto;
  text-align: center;
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.hero-toolbox .hero-eyebrow {
  margin-bottom: 20px;
}

.hero-toolbox .hero-title {
  max-width: 920px;
  margin: 0 auto 20px;
  background: none;
  -webkit-text-fill-color: currentColor;
  color: var(--ink);
  font-size: clamp(40px, 5.2vw, 68px);
  font-weight: 600;
  letter-spacing: -0.045em;
  line-height: 1.08;
}

.hero-toolbox .hero-title em {
  background: linear-gradient(100deg, #3b5bff 0%, #7c5cff 52%, #ff7a45 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  font-style: normal;
}

.hero-toolbox .hero-subtitle {
  max-width: 680px;
  margin: 0 auto 30px;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.8;
}

.hero-toolbox .hero-actions {
  justify-content: center;
  margin-bottom: 0;
}

.hero-stage {
  position: relative;
  width: min(100%, 1080px);
  margin: 38px auto 0;
  padding: 62px 30px 30px;
  border: 1px solid rgba(255, 255, 255, 0.78);
  border-radius: 30px;
  background:
      linear-gradient(135deg, rgba(255, 255, 255, 0.78) 0%, rgba(255, 255, 255, 0.48) 52%, rgba(255, 247, 242, 0.66) 100%);
  box-shadow:
      0 2px 0 rgba(255, 255, 255, 0.8) inset,
      0 36px 80px -56px rgba(11, 16, 32, 0.46);
  backdrop-filter: blur(18px);
  overflow: hidden;
}

.hero-stage-grid {
  position: absolute;
  inset: 0;
  background:
      linear-gradient(rgba(59, 91, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59, 91, 255, 0.05) 1px, transparent 1px);
  background-size: 38px 38px;
  mask-image: radial-gradient(ellipse 66% 70% at 50% 52%, #000 10%, transparent 78%);
  -webkit-mask-image: radial-gradient(ellipse 66% 70% at 50% 52%, #000 10%, transparent 78%);
}

.hero-stage-glow {
  position: absolute;
  left: 50%;
  top: 56%;
  width: 620px;
  height: 220px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(59, 91, 255, 0.13), rgba(124, 92, 255, 0.08) 42%, transparent 70%);
  filter: blur(24px);
}

.hero-stage-ring {
  position: absolute;
  left: 50%;
  top: 56%;
  border: 1px solid rgba(59, 91, 255, 0.12);
  border-radius: 50%;
  transform: translate(-50%, -50%) rotate(-12deg);
  pointer-events: none;
}

.hero-stage-ring--one {
  width: 610px;
  height: 190px;
}

.hero-stage-ring--two {
  width: 430px;
  height: 126px;
  border-color: rgba(255, 122, 69, 0.14);
  transform: translate(-50%, -50%) rotate(9deg);
}

.hero-stage-chip {
  position: absolute;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  box-shadow: 0 10px 24px -18px rgba(11, 16, 32, 0.35);
  color: var(--brand);
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.1em;
  backdrop-filter: blur(8px);
}

.hero-stage-chip--aip {
  left: 26px;
  top: 24px;
}

.hero-stage-chip--exe {
  right: 26px;
  top: 24px;
  color: var(--accent-strong);
}

.hero-stage-label {
  position: relative;
  z-index: 2;
  display: block;
  margin-top: 22px;
  color: var(--subtle);
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.16em;
  text-align: center;
}

.hero-dock {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: max-content;
  max-width: 100%;
  margin: 0 auto;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.9) inset,
      0 28px 60px -42px rgba(11, 16, 32, 0.42);
  backdrop-filter: blur(14px);
}

.dock-tile {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border: 1px solid #e6ebf3;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 5px 12px -7px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.dock-tile:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px -14px rgba(15, 23, 42, 0.35);
}

.dock-tile svg {
  width: 26px;
  height: 26px;
}

.dock-tile--blue { color: #2f5bff; background: #f2f5ff; }
.dock-tile--cyan { color: #0891b2; background: #effbfd; }
.dock-tile--teal { color: #0f766e; background: #effbf7; }
.dock-tile--orange { color: #c2410c; background: #fff7ed; }
.dock-tile--violet { color: #7c3aed; background: #f7f2ff; }
.dock-tile--slate { color: #475569; background: #f7f9fc; }

.dock-tile--more {
  border-style: dashed;
  background: transparent;
  box-shadow: none;
  color: #94a3b8;
  font-size: 22px;
  font-weight: 300;
}

.hero-meta {
  margin: 18px 0 0;
  color: var(--subtle);
  font-size: 11px;
  letter-spacing: 0.08em;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
  animation: fadeInUp 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.hero-eyebrow {
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  color: #2f5bff;
  background: rgba(47, 91, 255, 0.08);
  padding: 7px 18px;
  border-radius: 100px;
  margin-bottom: 24px;
  border: 1px solid rgba(47, 91, 255, 0.18);
}

.hero-title {
  font-size: clamp(2.4rem, 6vw, 4.5rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin: 0 0 24px;
  background: linear-gradient(135deg, #1e293b 0%, #2f5bff 60%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-title-accent {
  background: linear-gradient(135deg, #2f5bff 0%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.12rem;
  color: #475569;
  max-width: 680px;
  margin: 0 auto 40px;
  line-height: 1.7;
}

.hero-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 64px;
}

.cta-button {
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  color: white;
  font-weight: 600;
  border: none;
  padding: 16px 36px;
  font-size: 1.05rem;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 12px 28px rgba(47, 91, 255, 0.35);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.cta-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 38px rgba(47, 91, 255, 0.45);
}

.cta-button .cta-icon {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  font-size: 1rem;
}

.cta-button:hover .cta-icon {
  transform: translateX(4px);
}

.cta-button-secondary {
  background: white;
  color: #2f5bff;
  font-weight: 600;
  border: 1px solid rgba(47, 91, 255, 0.3);
  padding: 16px 32px;
  font-size: 1.05rem;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: background 0.4s ease, border-color 0.4s ease, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.cta-button-secondary:hover {
  background: rgba(47, 91, 255, 0.06);
  border-color: #2f5bff;
  transform: translateY(-4px);
}

.cta-button-secondary .cta-icon {
  font-size: 1rem;
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.cta-button-secondary:hover .cta-icon {
  transform: translateX(4px);
}

.hero-stats {
  display: flex;
  gap: 36px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.hero-stat-num {
  font-size: 2.3rem;
  font-weight: 800;
  line-height: 1;
  background: linear-gradient(135deg, #2f5bff 0%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-stat-label {
  font-size: 0.88rem;
  color: #475569;
  margin-top: 6px;
  letter-spacing: 0.04em;
}

.hero-stat-divider {
  width: 1px;
  height: 36px;
  background: linear-gradient(180deg, transparent, rgba(47, 91, 255, 0.25), transparent);
}

/* ============ 通用 Section Header ============ */
.section-header {
  text-align: center;
  margin-bottom: 56px;
}

.section-title {
  font-size: clamp(1.875rem, 4vw, 2.25rem);
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.025em;
}

.gradient-text {
  background: linear-gradient(135deg, #6296ea 0%, #72a2f1 60%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1rem;
  color: #64748b;
  max-width: 600px;
  margin: 16px auto 0;
  line-height: 1.6;
}

.section-divider {
  width: 128px;
  height: 1px;
  margin: 24px auto 0;
  background: linear-gradient(90deg, #0000, #0ea5e933, #f59e0b26, #0000);
}

/* 精选工具 — 全新样式 */
.tools-section {
  padding: 80px 0;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
}

.tools-section::before {
  content: '';
  position: absolute;
  top: 40px;
  left: -10%;
  width: 50%;
  height: 400px;
  background: radial-gradient(ellipse, rgba(47, 91, 255, 0.08), transparent 70%);
  filter: blur(40px);
  pointer-events: none;
  z-index: 0;
}

.tools-section-inner {
  position: relative;
  z-index: 1;
}

.home-section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 32px;
}

.home-section-copy {
  max-width: 680px;
}

.home-eyebrow {
  display: block;
  margin-bottom: 10px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.home-title {
  margin: 0 0 12px;
  color: #0f172a;
  font-size: clamp(30px, 3.6vw, 46px);
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1.08;
}

.home-lead {
  max-width: 600px;
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.8;
}

.home-section-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  gap: 2px;
  padding-bottom: 4px;
}

.home-count {
  color: #0f172a;
  font-size: 48px;
  font-weight: 800;
  letter-spacing: -0.06em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.home-count-label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
}

.home-text-link {
  margin-top: 10px;
  color: #2f5bff;
  font-size: 12px;
  font-weight: 650;
  text-decoration: none;
}

.home-text-link:hover {
  color: #f59e0b;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.home-tools-loading,
.home-tools-empty {
  display: grid;
  place-items: center;
  min-height: 120px;
  padding: 12px 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #64748b;
  font-size: 0.86rem;
}

.home-tools-empty p {
  margin: 0;
}

.home-tools-empty a {
  color: #2f5bff;
  font-weight: 600;
  text-decoration: none;
}

.tools-more {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.tools-more a {
  color: #2f5bff;
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
}

.tools-more a:hover {
  color: #f59e0b;
}

/* —— 工具卡片 —— */
.tool-card {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.5);
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  cursor: pointer;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: var(--card-delay, 0s);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
  box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1),
  border-color 0.3s ease;
}

.tool-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.10), 0 4px 16px rgba(47, 91, 255, 0.08);
  border-color: rgba(47, 91, 255, 0.20);
}

/* —— 角标 —— */
.tool-badge {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.tool-badge--hot {
  background: linear-gradient(to right, #2f5bff, #5b7cff);
}
.tool-badge--pro {
  background: linear-gradient(to right, #f59e0b, #fbbf24);
}
.tool-badge--rec {
  background: linear-gradient(to right, #6366f1, #818cf8);
}
.tool-badge--new {
  background: linear-gradient(to right, #22c55e, #34d399);
}

.tool-badge--aip {
  background: linear-gradient(to right, #2f5bff, #5b7cff);
}

.tool-trial {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 9px;
  border: 1px solid rgba(180, 83, 9, 0.3);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 18px -14px rgba(11, 16, 32, 0.45);
  color: #7c2d12;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.3;
  backdrop-filter: blur(10px);
}

.tool-trial::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d97706;
  box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.13);
}

.tool-trial.is-free {
  border-color: rgba(22, 163, 74, 0.3);
  background: rgba(248, 255, 251, 0.97);
  color: #166534;
}

.tool-trial.is-free::before {
  background: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.13);
}

/* —— 图片区域 —— */
.tool-image-wrap {
  position: relative;
  width: 100%;
  height: 192px;
  overflow: hidden;
  background: linear-gradient(to bottom right, #eef3ff, #fff7ed);
}

.tool-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.home-tool-cover {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.home-tool-gif {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.tool-card:hover .tool-image,
.tool-card:hover .home-tool-gif {
  transform: scale(1.06);
}

/* 白色蒙层 (悬停) */
.tool-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.94), transparent 50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.tool-card:hover .tool-image-overlay {
  opacity: 1;
}

/* 底部图标 (悬停出现) */
.tool-image-icon {
  position: absolute;
  right: 16px;
  bottom: 16px;
  left: auto;
  transform: translateY(10px);
  z-index: 2;
  opacity: 0;
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.tool-card:hover .tool-image-icon {
  opacity: 1;
  transform: translateY(0);
}

.tool-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.2rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.20);
}

.tool-icon-circle--blue {
  background: linear-gradient(to bottom right, #2f5bff, #5b7cff);
}
.tool-icon-circle--orange {
  background: linear-gradient(to bottom right, #f59e0b, #fbbf24);
}
.tool-icon-circle--indigo {
  background: linear-gradient(to bottom right, #6366f1, #818cf8);
}
.tool-icon-circle--warm {
  background: linear-gradient(to bottom right, #d97706, #fbbf24);
}

/* —— 内容区 —— */
.tool-body {
  padding: 18px 20px 20px;
}

.tool-name {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  transition: color 0.3s ease;
}

.tool-card:hover .tool-name {
  color: #1f43cc;
}

.tool-desc {
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.home-tool-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}

.home-tool-chips span {
  padding: 3px 7px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 500;
  line-height: 1.3;
}

/* —— 底部操作行 —— */
.tool-action {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tool-action-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 0.72rem;
  font-weight: 500;
}

.tool-action-label--blue {
  background: #eef3ff;
  color: #1f43cc;
}
.tool-action-label--orange {
  background: #fff7ed;
  color: #d97706;
}
.tool-action-label--indigo {
  background: #eef3ff;
  color: #4f46e5;
}
.tool-action-label--warm {
  background: #fffbeb;
  color: #d97706;
}

.tool-action-arrow {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.tool-action-arrow--blue {
  background: #eef3ff;
  color: #2f5bff;
}
.tool-action-arrow--orange {
  background: #fff7ed;
  color: #f59e0b;
}
.tool-action-arrow--indigo {
  background: #eef3ff;
  color: #6366f1;
}
.tool-action-arrow--warm {
  background: #fffbeb;
  color: #d97706;
}

.tool-card:hover .tool-action-arrow {
  opacity: 1;
}

.tool-card:hover .tool-action-arrow--blue {
  background: #2f5bff;
  color: #fff;
}
.tool-card:hover .tool-action-arrow--orange {
  background: #f59e0b;
  color: #fff;
}
.tool-card:hover .tool-action-arrow--indigo {
  background: #6366f1;
  color: #fff;
}
.tool-card:hover .tool-action-arrow--warm {
  background: #d97706;
  color: #fff;
}

/* —— 底部色条 —— */
.tool-bar {
  height: 2px;
  width: 100%;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.tool-bar--blue {
  background: linear-gradient(to right, #2f5bff, #5b7cff);
}
.tool-bar--orange {
  background: linear-gradient(to right, #f59e0b, #fbbf24);
}
.tool-bar--indigo {
  background: linear-gradient(to right, #6366f1, #818cf8);
}
.tool-bar--warm {
  background: linear-gradient(to right, #d97706, #fbbf24);
}

.tool-card:hover .tool-bar {
  transform: scaleX(1);
}


/* 解决方案 */
.solutions-section {
  padding: 80px 0 100px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
}

.solutions-section-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.solutions-section::before {
  content: '';
  position: absolute;
  top: 20px;
  right: -10%;
  width: 55%;
  height: 450px;
  background: radial-gradient(ellipse, rgba(245, 158, 11, 0.07), transparent 70%);
  filter: blur(40px);
  pointer-events: none;
  z-index: 0;
}

.solutions-section-inner {
  position: relative;
  z-index: 1;
}

.solutions-editorial {
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
  gap: 56px;
  align-items: start;
}

.solutions-intro {
  position: sticky;
  top: 112px;
  padding-top: 6px;
}

.solutions-title {
  max-width: 440px;
}

.solutions-title span {
  color: #2f5bff;
}

.solutions-link {
  display: inline-flex;
  margin-top: 18px;
}

.solutions-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.solutions-grid--editorial {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.solutions-grid--editorial .solution-card {
  display: flex;
  flex-direction: column;
  padding: 24px;
  border-radius: 18px;
}

.solution-index {
  position: absolute;
  top: 18px;
  right: 20px;
  z-index: 3;
  color: #cbd5e1;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.solutions-grid--editorial .solution-icon {
  width: 52px;
  height: 52px;
  margin-bottom: 18px;
  border-radius: 14px;
  font-size: 1.25rem;
}

.solutions-grid--editorial .solution-title {
  margin-bottom: 8px;
  font-size: 1.02rem;
}

.solutions-grid--editorial .solution-desc {
  margin-bottom: 18px;
  font-size: 0.8rem;
  line-height: 1.65;
}

.solution-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: auto;
  color: #2f5bff;
  font-size: 12px;
  font-weight: 650;
  transition: color 0.2s ease, gap 0.2s ease;
}

.solution-card:hover .solution-link {
  gap: 8px;
  color: #f59e0b;
}

/* —— 解决方案卡片 —— */
.solution-card {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 28px 32px;
  border: 1px solid rgba(226, 232, 240, 0.5);
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: var(--card-delay, 0s);
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1),
  box-shadow 0.8s cubic-bezier(0.16, 1, 0.3, 1),
  border-color 0.7s ease;
}

.solution-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.10), 0 4px 16px rgba(47, 91, 255, 0.08);
  border-color: rgba(47, 91, 255, 0.20);
}

/* —— 顶部色条 —— */
.solution-top-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2;
}

.solution-top-bar--blue {
  background: linear-gradient(to right, #2f5bff, #5b7cff);
}
.solution-top-bar--orange {
  background: linear-gradient(to right, #f59e0b, #fbbf24);
}
.solution-top-bar--rose {
  background: linear-gradient(to right, #f43f5e, #fb7185);
}
.solution-top-bar--purple {
  background: linear-gradient(to right, #8b5cf6, #a78bfa);
}

.solution-card:hover .solution-top-bar {
  transform: scaleX(1);
}

/* —— 图标 —— */
.solution-icon {
  width: 68px;
  height: 68px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.6rem;
  margin-bottom: 24px;
  transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  z-index: 1;
}

.solution-icon--blue {
  background: linear-gradient(to bottom right, #2f5bff, #5b7cff);
  box-shadow: 0 8px 20px rgba(47, 91, 255, 0.30);
}
.solution-icon--orange {
  background: linear-gradient(to bottom right, #f59e0b, #fbbf24);
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.30);
}
.solution-icon--rose {
  background: linear-gradient(to bottom right, #f43f5e, #fb7185);
  box-shadow: 0 8px 20px rgba(244, 63, 94, 0.30);
}
.solution-icon--purple {
  background: linear-gradient(to bottom right, #8b5cf6, #a78bfa);
  box-shadow: 0 8px 20px rgba(139, 92, 246, 0.30);
}

.solution-card:hover .solution-icon {
  transform: scale(1.10) rotate(3deg);
}

/* —— 标题 & 描述 —— */
.solution-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 12px;
  color: #0f172a;
  position: relative;
  z-index: 1;
}

.solution-desc {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.7;
  margin: 0 0 24px;
  position: relative;
  z-index: 1;
}

/* —— 箭头 —— */
.solution-arrow {
  position: relative;
  z-index: 1;
}

.solution-arrow-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
}

.solution-arrow-circle--blue {
  background: #eef3ff;
  color: #2f5bff;
}
.solution-arrow-circle--orange {
  background: #fff7ed;
  color: #f59e0b;
}
.solution-arrow-circle--rose {
  background: #fff1f2;
  color: #f43f5e;
}
.solution-arrow-circle--purple {
  background: #f5f3ff;
  color: #8b5cf6;
}

.solution-card:hover .solution-arrow-circle--blue {
  background: #2f5bff;
  color: #fff;
  transform: translateX(4px);
}
.solution-card:hover .solution-arrow-circle--orange {
  background: #f59e0b;
  color: #fff;
  transform: translateX(4px);
}
.solution-card:hover .solution-arrow-circle--rose {
  background: #f43f5e;
  color: #fff;
  transform: translateX(4px);
}
.solution-card:hover .solution-arrow-circle--purple {
  background: #8b5cf6;
  color: #fff;
  transform: translateX(4px);
}

/* 动画 */
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式 */
@media (max-width: 1200px) {
  .tools-grid,
  .solutions-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
  }

  .hero-section {
    padding: 90px 20px 100px;
  }
}

@media (max-width: 1024px) {
  .hero-toolbox .hero-title {
    font-size: clamp(44px, 6vw, 64px);
  }

  .hero-editorial-inner {
    grid-template-columns: 1fr;
    gap: 38px;
  }

  .hero-editorial .hero-copy {
    max-width: 720px;
  }

  .hero-visual {
    max-width: 560px;
  }

  .solutions-editorial {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .solutions-intro {
    position: static;
    padding-top: 0;
  }

  .home-section-head {
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .hero-toolbox.hero-section {
    padding: 52px 16px 58px;
  }

  .hero-toolbox .hero-title {
    font-size: clamp(36px, 9.6vw, 48px);
  }

  .hero-toolbox .hero-subtitle {
    font-size: 14px;
  }

  .hero-toolbox .hero-actions {
    margin-bottom: 0;
  }

  .hero-stage {
    margin-top: 28px;
    padding: 50px 14px 22px;
    border-radius: 22px;
  }

  .hero-stage-chip {
    display: none;
  }

  .hero-stage-ring--one {
    width: 420px;
    height: 140px;
  }

  .hero-stage-ring--two {
    width: 300px;
    height: 96px;
  }

  .hero-stage-glow {
    width: 420px;
    height: 170px;
  }

  .hero-stage .hero-dock {
    flex-wrap: wrap;
    width: auto;
    max-width: 290px;
  }

  .hero-dock {
    gap: 7px;
    padding: 8px;
    border-radius: 16px;
  }

  .dock-tile {
    width: 40px;
    height: 40px;
    border-radius: 11px;
  }

  .dock-tile svg {
    width: 20px;
    height: 20px;
  }

  .dock-tile--more {
    font-size: 18px;
  }

  .hero-meta {
    margin-top: 18px;
    font-size: 10px;
  }

  .hero-editorial {
    padding: 50px 16px 56px;
  }

  .hero-editorial .hero-title {
    font-size: clamp(36px, 10vw, 48px);
  }

  .hero-editorial .hero-actions {
    margin-bottom: 30px;
  }

  .hero-visual {
    display: none;
  }

  .home-section-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .home-section-side {
    align-items: flex-start;
  }

  .home-count {
    font-size: 40px;
  }

  .solutions-grid--editorial {
    grid-template-columns: 1fr;
  }

  .hero-section {
    padding: 60px 20px 70px;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 48px;
  }

  .cta-button,
  .cta-button-secondary {
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
    justify-content: center;
  }

  .hero-stats {
    gap: 20px;
  }
  .hero-stat-num {
    font-size: 1.7rem;
  }
  .hero-stat-label {
    font-size: 0.8rem;
  }
  .hero-stat-divider {
    display: none;
  }

  .tools-section,
  .solutions-section {
    padding: 50px 16px;
  }
  .solutions-section {
    padding-bottom: 70px;
  }

  .tools-grid,
  .solutions-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .tool-image-wrap {
    height: 200px;
  }

  .solution-card {
    padding: 28px 22px;
  }
  .solution-icon {
    width: 72px;
    height: 72px;
    margin-bottom: 20px;
    font-size: 1.8rem;
  }
}

@media (max-width: 576px) {
  .hero-section {
    padding: 50px 16px 60px;
  }

  .hero-eyebrow {
    font-size: 0.72rem;
    padding: 6px 14px;
  }
  .hero-actions {
    margin-bottom: 40px;
  }
  .cta-button,
  .cta-button-secondary {
    padding: 14px 24px;
    font-size: 0.98rem;
  }
  .hero-stats {
    gap: 14px;
  }
  .hero-stat-num {
    font-size: 1.5rem;
  }

  .tool-body {
    padding: 14px 16px 16px;
  }
  .tool-name {
    font-size: 0.95rem;
  }

  .solution-card {
    padding: 24px 18px;
  }
  .solution-title {
    font-size: 1.05rem;
  }
  .solution-desc {
    font-size: 0.85rem;
  }
}

@media (min-width: 1440px) {
  .tools-section,
  .solutions-section {
    max-width: 1200px;
  }
  .hero-content {
    max-width: 1200px;
  }
}
</style>

<style scoped>
/* 2026 visual refresh: calmer surfaces, stronger type, less glow */
.hero-section {
  background: transparent;
}

.hero-blob,
.hero-blob-small {
  opacity: 0.22;
  filter: blur(70px);
}

.hero-title {
  color: var(--ink);
  letter-spacing: -0.045em;
  font-weight: 850;
}

.hero-title-accent {
  color: var(--brand);
  background: none;
  -webkit-text-fill-color: currentColor;
}

.hero-subtitle {
  color: var(--muted);
  font-size: 16px;
  line-height: 1.8;
}

.hero-eyebrow {
  color: var(--muted);
  letter-spacing: 0.2em;
}

.cta-button {
  background: var(--brand);
  border-radius: var(--radius-sm);
  box-shadow: 0 14px 30px -18px rgba(47, 91, 255, 0.55);
}

.cta-button-secondary {
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  box-shadow: none;
}

.hero-stat-num {
  color: var(--ink);
  letter-spacing: -0.03em;
}

.hero-stat-label {
  color: var(--muted);
}

.tools-section,
.solutions-section {
  background: var(--canvas);
}

.section-title {
  color: var(--ink);
  letter-spacing: -0.04em;
}

.section-subtitle {
  color: var(--muted);
}

.tool-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.tool-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-soft);
}

.tool-name {
  color: var(--ink);
  letter-spacing: -0.02em;
}

.tool-desc {
  color: var(--muted);
}

.tool-bar {
  height: 3px;
  opacity: 0.85;
}

/* 精选工具：编辑式标题区，减少居中排版的“模板感” */
.section-header--editorial {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  text-align: left;
  margin-bottom: 42px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--line);
}

.section-header--editorial .section-heading {
  min-width: 0;
}

.section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: var(--muted);
}

.section-kicker-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--brand);
  box-shadow: 0 0 0 4px rgba(47, 91, 255, 0.08);
}

.section-kicker-index {
  color: #b6c0d4;
}

.section-header--editorial .section-title {
  font-size: clamp(2rem, 4.2vw, 3rem);
  line-height: 1.06;
}

.section-header--editorial .section-subtitle {
  margin: 14px 0 0;
  max-width: 560px;
}

.section-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding-bottom: 2px;
}

.section-meta-num {
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  font-weight: 800;
  line-height: 0.9;
  letter-spacing: -0.07em;
  color: var(--ink);
}

.section-meta-label {
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--muted);
}

@media (max-width: 680px) {
  .section-header--editorial {
    align-items: flex-start;
    flex-direction: column;
    gap: 18px;
    margin-bottom: 28px;
    padding-bottom: 18px;
  }

  .section-meta {
    align-self: flex-end;
  }

  .section-meta-num {
    font-size: 2.4rem;
  }
}
</style>